import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button as MantineButton, Stack, Text } from '@mantine/core';
import { AddressElement, useElements } from '@stripe/react-stripe-js';
import { ReactComponent as BackIcon } from '../../assets/images/icons/back.svg';
import { useLocale } from '../../contexts/LocaleContext';
import StripeElementsProvider from '../../lib/StripeElementsProvider';
import type { StripeAddressValue } from '../StripeCheckout';
import styles from '../ModalEditShipping/EditShipping.module.scss';
import sheetStyles from './InAppCheckoutSheet.module.scss';

interface AccountAddress {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone?: string | null;
}

interface InAppShippingPanelProps {
  account?: { firstName?: string | null; lastName?: string | null; address?: AccountAddress | null } | null;
  onSubmit: (value: StripeAddressValue) => void;
  onCancel: () => void;
}

function ShippingForm({ account, onSubmit }: Pick<InAppShippingPanelProps, 'account' | 'onSubmit'>) {
  const { t } = useTranslation();
  const { country } = useLocale();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const address = account?.address;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const addressElement = elements?.getElement('address');
    if (!addressElement) return;
    setSubmitting(true);
    setError(null);
    const { complete, value } = await addressElement.getValue();
    setSubmitting(false);
    if (!complete) {
      setError(t('please_fill_in_all_required_fields'));
      return;
    }
    onSubmit(value);
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)}>
      <Stack gap={16}>
        <AddressElement
          options={{
            mode: 'shipping',
            autocomplete: { mode: 'automatic' },
            display: { name: 'split' },
            fields: { phone: 'always' },
            validation: { phone: { required: 'always' } },
            allowedCountries: [country],
            defaultValues: {
              firstName: account?.firstName ?? undefined,
              lastName: account?.lastName ?? undefined,
              phone: address?.phone ?? undefined,
              address: address
                ? {
                    line1: address.line1,
                    line2: address.line2 ?? undefined,
                    city: address.city,
                    state: address.state,
                    postal_code: address.zip,
                    country: address.country || country,
                  }
                : { country },
            },
          }}
        />
        {error && (
          <Text fz={14} c="red">
            {error}
          </Text>
        )}
        <MantineButton type="submit" color="dark" radius="lg" loading={submitting} disabled={!elements}>
          {t('update')}
        </MantineButton>
      </Stack>
    </form>
  );
}

export default function InAppShippingPanel({ account, onSubmit, onCancel }: InAppShippingPanelProps) {
  const { t } = useTranslation();
  const { currency } = useLocale();
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <BackIcon onClick={onCancel} className={styles.backButton} />
        <h3>{t(account?.address ? 'edit_shipping' : 'add_shipping_address')}</h3>
        <div className={styles.headerPlaceholder} />
      </div>
      <div className={sheetStyles.panelBody}>
        <StripeElementsProvider mode="setup" currency={currency}>
          <ShippingForm account={account} onSubmit={onSubmit} />
        </StripeElementsProvider>
      </div>
    </div>
  );
}
